'use client';

import React, { useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

import { useAuth } from 'src/context/auth';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faLeftFromBracket, faGear } from '@fortawesome/pro-solid-svg-icons';
import {
  faEnvelope,
  faScrewdriverWrench,
  faCog,
  faBook,
  faComments,
} from '@fortawesome/pro-regular-svg-icons';
import Link from 'next/link';
import { useSettingsRoutes } from 'src/hooks/settings';
import { useWebSocketNotificationsEnabled } from 'src/hooks/system';
import { NotificationIndicator } from 'src/components/Notifications';
import { useIntercom } from 'src/hooks/useIntercom';
import { useGetUnreadCountQuery } from 'src/api/redux';
import MenuChangesCallout from './MenuChangesCallout';

const UserDropdown = () => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = React.useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const notificationsEnabled = useWebSocketNotificationsEnabled();
  const { data: unreadData } = useGetUnreadCountQuery(undefined, {
    skip: !notificationsEnabled,
  });
  const hasUnread = (unreadData?.count ?? 0) > 0;
  const { show: showIntercom } = useIntercom();
  const { routes: settingsRoutes, adminRoutes } = useSettingsRoutes();

  // Get the first available settings route for this user
  const settingsHref = settingsRoutes[0]?.path ?? '/settings';
  // Get the first available admin route (if user has admin access)
  const adminHref = adminRoutes[0]?.path;
  const showAdminLink = adminRoutes.length > 0;

  const handleToggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Handle click outside to close dropdown
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        triggerRef.current &&
        dropdownRef.current &&
        !triggerRef.current.contains(event.target as Node) &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const getDropdownPosition = () => {
    if (!triggerRef.current) return { bottom: 0, left: 0 };
    const triggerRect = triggerRef.current.getBoundingClientRect();
    return {
      bottom: window.innerHeight - triggerRect.bottom,
      left: triggerRect.right + 5, // Position to the right of the trigger
    };
  };

  return (
    <>
      <div
        ref={triggerRef}
        role="button"
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-label="User menu"
        data-testid="user-dropdown-trigger"
        className="relative w-10 h-10 bg-sky-500 rounded-full hover:bg-sky-700 flex items-center justify-center cursor-pointer"
        onClick={handleToggle}
      >
        <Icon icon={faGear} size="sm" className="text-white" />
        {notificationsEnabled && <NotificationIndicator />}
      </div>
      {!isOpen && <MenuChangesCallout triggerRef={triggerRef} />}
      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            role="menu"
            aria-label="User menu"
            data-testid="user-dropdown-menu"
            style={getDropdownPosition()}
            className="w-56 p-1 inline-flex flex-col justify-start
            items-start gap-2 fixed z-[9999] bg-white border border-gray-200
            rounded-lg shadow-lg"
          >
            <div className="self-stretch p-2 rounded-lg inline-flex justify-between items-start">
              <div className="justify-start text-text-secondary text-sm font-semibold font-['Inter'] leading-5">
                {user.first_name} {user.last_name}
              </div>
              <Link
                href="/auth/logout?user_initiated=true"
                className="w-5 h-5 overflow-hidden flex items-center justify-center"
              >
                <Icon
                  icon={faLeftFromBracket}
                  size="sm"
                  className="w-4 h-3.5 text-secondary"
                />
              </Link>
            </div>
            <div className="self-stretch flex flex-col justify-start items-start">
              {showAdminLink && adminHref && (
                <Link
                  href={adminHref}
                  className="self-stretch p-2 rounded-lg inline-flex justify-start items-start gap-2 hover:bg-neutral-100 cursor-pointer"
                  onClick={handleClose}
                >
                  <div className="flex justify-start items-center gap-2">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <Icon
                        icon={faScrewdriverWrench}
                        className="text-secondary"
                      />
                    </div>
                    <div className="justify-start text-text-primary text-sm font-medium font-['Inter'] leading-5">
                      Admin
                    </div>
                  </div>
                </Link>
              )}
              <Link
                href={settingsHref}
                className="self-stretch p-2 rounded-lg inline-flex justify-start items-start gap-2 hover:bg-neutral-100 cursor-pointer"
                onClick={handleClose}
              >
                <div className="flex justify-start items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <Icon icon={faCog} className="text-secondary" />
                  </div>
                  <div className="justify-start text-text-primary text-sm font-medium font-['Inter'] leading-5">
                    Settings
                  </div>
                </div>
              </Link>
              {notificationsEnabled && (
                <Link
                  href="/notifications"
                  role="menuitem"
                  aria-label={hasUnread ? 'Notifications - unread' : 'Notifications'}
                  data-testid="user-dropdown-notifications"
                  className="self-stretch p-2 rounded-lg inline-flex justify-start items-start gap-2 hover:bg-neutral-100 cursor-pointer"
                  onClick={handleClose}
                >
                  <div className="flex justify-start items-center gap-2">
                    <div className="relative w-5 h-5 flex items-center justify-center">
                      <Icon icon={faEnvelope} className="text-secondary" />
                      {hasUnread && (
                        <span
                          aria-hidden="true"
                          data-testid="user-dropdown-unread-dot"
                          className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-red-500"
                        />
                      )}
                    </div>
                    <div className="justify-start text-text-primary text-sm font-medium font-['Inter'] leading-5">
                      Notifications
                    </div>
                  </div>
                </Link>
              )}
              <a
                href="https://help.medscout.io/en"
                target="_blank"
                rel="noopener noreferrer"
                role="menuitem"
                aria-label="Help Center"
                data-testid="user-dropdown-help-center"
                className="self-stretch p-2 rounded-lg inline-flex justify-start items-start gap-2 hover:bg-neutral-100 cursor-pointer"
                onClick={handleClose}
              >
                <div className="flex justify-start items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <Icon icon={faBook} className="text-secondary" />
                  </div>
                  <div className="justify-start text-text-primary text-sm font-medium font-['Inter'] leading-5">
                    Help Center
                  </div>
                </div>
              </a>
              <button
                type="button"
                className="self-stretch p-2 rounded-lg inline-flex justify-start items-start gap-2 hover:bg-neutral-100 cursor-pointer"
                onClick={() => {
                  showIntercom();
                  handleClose();
                }}
                aria-label="Show chat support"
                data-testid="user-dropdown-chat-support"
              >
                <div className="flex justify-start items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <Icon icon={faComments} className="text-secondary" />
                  </div>
                  <div className="justify-start text-text-primary text-sm font-medium font-['Inter'] leading-5">
                    Chat Support
                  </div>
                </div>
              </button>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default UserDropdown;
